(() => {
  if (customElements.get('maison-collection-results')) return;

  const UPDATE_LINK_SELECTOR = [
    '.maison-price-filter__preset',
    '.maison-price-filter__reset',
    '.maison-metal-filter__reset',
    '.maison-collection__pagination a',
    '[data-maison-collection-update]',
  ].join(',');

  class MaisonCollectionResults extends HTMLElement {
    constructor() {
      super();
      this.requestSequence = 0;
      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.status = this.querySelector('[data-maison-results-status]');
      this.error = this.querySelector('[data-maison-results-error]');
      this.retry = this.querySelector('[data-maison-results-retry]');
      this.addEventListener('click', this.handleClick);
      this.addEventListener('submit', this.handleSubmit);
      window.addEventListener('popstate', this.handlePopState);
    }

    disconnectedCallback() {
      this.connected = false;
      this.abortController?.abort();
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleSubmit);
      window.removeEventListener('popstate', this.handlePopState);
    }

    get sectionElement() {
      return this.querySelector('[data-maison-collection]');
    }

    handleClick(event) {
      const retry = event.target.closest('[data-maison-results-retry]');
      if (retry && this.contains(retry)) {
        event.preventDefault();
        if (this.failedUrl) this.update(this.failedUrl, this.failedOptions);
        return;
      }

      const anchor = event.target.closest(UPDATE_LINK_SELECTOR);
      if (!anchor || !this.contains(anchor) || !this.shouldHandleClick(event, anchor)) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      const action = anchor.closest('.maison-collection__pagination') ? 'pagination' : 'filter';
      this.update(url, { action, history: 'push' });
    }

    handleSubmit(event) {
      const form = event.target;
      if (event.defaultPrevented || !(form instanceof HTMLFormElement) || !this.contains(form)) return;
      if ((form.method || 'get').toLowerCase() !== 'get') return;

      event.preventDefault();
      const url = new URL(form.action, window.location.href);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, name) => {
        if (typeof value === 'string' && value !== '') params.append(name, value);
      });
      params.delete('page');
      url.search = params.toString();

      form.querySelectorAll('[data-maison-price-input][disabled]').forEach((input) => {
        input.disabled = false;
      });

      this.update(url, { action: 'filter', history: 'push' });
    }

    handlePopState() {
      this.update(new URL(window.location.href), { action: 'history', history: 'none' });
    }

    shouldHandleClick(event, anchor) {
      if (event.defaultPrevented || event.button !== 0) return false;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
      if (anchor.target && anchor.target !== '_self') return false;
      if (anchor.hasAttribute('download')) return false;
      return true;
    }

    async update(url, options = {}) {
      const normalizedUrl = new URL(url, window.location.href);
      normalizedUrl.hash = '';
      const requestUrl = new URL(normalizedUrl);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);

      this.abortController?.abort();
      this.abortController = new AbortController();
      const requestId = ++this.requestSequence;
      const requestOptions = {
        action: options.action || 'filter',
        history: options.history || 'push',
      };

      this.failedUrl = normalizedUrl;
      this.failedOptions = requestOptions;
      this.setLoading(true);

      try {
        const response = await fetch(requestUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Section request failed with ${response.status}`);

        const html = await response.text();
        if (requestId !== this.requestSequence) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextSection = parsed.querySelector('maison-collection-results [data-maison-collection]');
        if (!nextSection) throw new Error('Collection section was missing from the response');

        if (requestOptions.history === 'push') {
          window.history.pushState({ maisonCollection: true }, '', normalizedUrl);
        }
        this.sectionElement.replaceWith(document.importNode(nextSection, true));

        this.failedUrl = null;
        this.failedOptions = null;
        this.setLoading(false);
        this.hideError();
        this.announce(this.dataset.updatedMessage);
        this.restoreFocus(requestOptions.action);
        this.dataset.updateCount = String(Number(this.dataset.updateCount || 0) + 1);
        this.dispatchEvent(new CustomEvent('maison:collection:updated', {
          bubbles: true,
          detail: { url: normalizedUrl.href },
        }));
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestSequence) return;
        this.setLoading(false);
        this.showError();
      }
    }

    setLoading(isLoading) {
      const section = this.sectionElement;
      if (isLoading) {
        this.dataset.loading = 'true';
        section?.setAttribute('aria-busy', 'true');
        this.announce(this.dataset.loadingMessage);
      } else {
        delete this.dataset.loading;
        section?.setAttribute('aria-busy', 'false');
      }
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message || '';
      });
    }

    showError() {
      if (this.error) this.error.hidden = false;
      this.announce(this.dataset.errorMessage);
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }

    restoreFocus(action) {
      if (action === 'history') return;
      const heading = this.querySelector('[data-maison-results-heading]');
      if (!heading) return;
      heading.focus({ preventScroll: true });
      if (action === 'pagination') heading.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }

  customElements.define('maison-collection-results', MaisonCollectionResults);
})();
